import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import BankDetailsFields from "../FormComponents/BankDetailsFields";
import {
  fetchBankDetailsByParent,
  syncBankDetailsEntries,
  deleteBankDetailsData,
} from "../../services/bankDetailsService";

/**
 * Add Bank Account Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onBankAdded - Callback after bank is added successfully
 */
export const AddBankDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onBankAdded,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const handleClear = () => {
    setFormData({
      accountholdername1: "",
      accountnumber1: "",
      ifsc1: "",
      accounttype1: "",
      bankname1: "",
      branch1: "",
      bankcountry1: "",
      status1: "Active",
      bankdescription1: "",
      image1: "",
    });
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const bankPayload = [
        {
          accountholdername: formData.accountholdername1,
          accountnumber: formData.accountnumber1,
          ifsc: formData.ifsc1,
          accounttype: formData.accounttype1,
          bankname: formData.bankname1,
          branch: formData.branch1,
          bankcountry: formData.bankcountry1,
          status: formData.status1 || "Active",
          bankdescription: formData.bankdescription1,
          image: formData.image1,
        },
      ];

      await syncBankDetailsEntries(sessionId, userId, bankPayload, []);

      // Fetch updated list
      const updatedBankEntries = await fetchBankDetailsByParent(sessionId, userId);
      
      if (onBankAdded) {
        onBankAdded(updatedBankEntries);
      }

      handleClear();
      onClose();
      
      if (onSuccess) {
        onSuccess("Bank account added successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to add bank account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      handleClear();
      onClose();
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="success" sx={{ mr: 1 }}>
              add_circle
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Add New Bank Account
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <BankDetailsFields
            formData={{
              accountholdername1: formData.accountholdername1 || "",
              accountnumber1: formData.accountnumber1 || "",
              ifsc1: formData.ifsc1 || "",
              accounttype1: formData.accounttype1 || "",
              bankname1: formData.bankname1 || "",
              branch1: formData.branch1 || "",
              bankcountry1: formData.bankcountry1 || "",
              status1: formData.status1 || "Active",
              bankdescription1: formData.bankdescription1 || "",
              image1: formData.image1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={{}}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="success"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            "Add Bank Account"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit Bank Account Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {object} bankEntry - Bank entry to edit
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onBankUpdated - Callback after bank is updated successfully
 */
export const EditBankDialog = ({
  open,
  onClose,
  bankEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onBankUpdated,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Load bank data when dialog opens
  React.useEffect(() => {
    if (open && bankEntry) {
      setFormData({
        accountholdername1: bankEntry.accountholdername || "",
        accountnumber1: bankEntry.accountnumber || "",
        ifsc1: bankEntry.ifsc || "",
        accounttype1: bankEntry.accounttype || "",
        bankname1: bankEntry.bankname || "",
        branch1: bankEntry.branch || "",
        bankcountry1: bankEntry.bankcountry || "",
        status1: bankEntry.status || "Active",
        bankdescription1: bankEntry.description || "",
        image1: bankEntry.image || "",
      });
    }
  }, [open, bankEntry]);

  const handleClear = () => {
    setFormData({
      accountholdername1: "",
      accountnumber1: "",
      ifsc1: "",
      accounttype1: "",
      bankname1: "",
      branch1: "",
      bankcountry1: "",
      status1: "Active",
      bankdescription1: "",
      image1: "",
    });
  };

  const handleSave = async () => {
    if (!bankEntry || !bankEntry.id) return;

    setIsLoading(true);
    try {
      const bankPayload = [
        {
          id: bankEntry.id,
          accountholdername: formData.accountholdername1,
          accountnumber: formData.accountnumber1,
          ifsc: formData.ifsc1,
          accounttype: formData.accounttype1,
          bankname: formData.bankname1,
          branch: formData.branch1,
          bankcountry: formData.bankcountry1,
          status: formData.status1 || "Active",
          bankdescription: formData.bankdescription1,
          image: formData.image1,
        },
      ];

      await syncBankDetailsEntries(sessionId, userId, bankPayload, []);

      // Fetch updated list
      const updatedBankEntries = await fetchBankDetailsByParent(sessionId, userId);
      
      if (onBankUpdated) {
        onBankUpdated(updatedBankEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("Bank account updated successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to update bank account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="info" sx={{ mr: 1 }}>
              edit
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Edit Bank Details
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <BankDetailsFields
            formData={{
              accountholdername1: formData.accountholdername1 || "",
              accountnumber1: formData.accountnumber1 || "",
              ifsc1: formData.ifsc1 || "",
              accounttype1: formData.accounttype1 || "",
              bankname1: formData.bankname1 || "",
              branch1: formData.branch1 || "",
              bankcountry1: formData.bankcountry1 || "",
              status1: formData.status1 || "Active",
              bankdescription1: formData.bankdescription1 || "",
              image1: formData.image1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={{}}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton
          onClick={handleClear}
          variant="outlined"
          color="warning"
          disabled={isLoading}
        >
          Clear
        </MDButton>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          color="success"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            "Update"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete Bank Account Confirmation Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {object} bankEntry - Bank entry to delete
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onBankDeleted - Callback after bank is deleted successfully
 */
export const DeleteBankDialog = ({
  open,
  onClose,
  bankEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onBankDeleted,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!bankEntry || !bankEntry.id) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await deleteBankDetailsData(sessionId, bankEntry.id);

      // Fetch updated list
      const updatedBankEntries = await fetchBankDetailsByParent(sessionId, userId);
      
      if (onBankDeleted) {
        onBankDeleted(updatedBankEntries);
      }

      onClose();
      
      if (onSuccess) {
        onSuccess("Bank account deleted successfully!");
      }
    } catch (error) {
      if (onError) {
        onError("Failed to delete bank account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center">
          <Icon color="error" sx={{ mr: 1 }}>
            warning
          </Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Confirm Deletion
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text">
          Are you sure you want to delete this bank account?
        </MDTypography>
        {bankEntry && (
          <MDBox
            mt={2}
            p={2}
            sx={{ backgroundColor: "grey.100", borderRadius: 1 }}
          >
            <MDTypography variant="body2" fontWeight="medium">
              Account Holder: {bankEntry.accountholdername || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Bank: {bankEntry.bankname || "N/A"}
            </MDTypography>
            <MDTypography variant="body2">
              Branch: {bankEntry.branch || "N/A"}
            </MDTypography>
          </MDBox>
        )}
        <MDTypography variant="caption" color="error" mt={2} display="block">
          This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton
          onClick={handleDialogClose}
          variant="outlined"
          color="dark"
          disabled={isLoading}
        >
          Cancel
        </MDButton>
        <MDButton
          onClick={handleConfirm}
          variant="gradient"
          color="error"
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
